import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const tiers = [
  {
    tier: 'Tier 1',
    name: 'AI Readiness Assessment',
    time: 'One-time · 2–4 weeks',
    color: '#378ADD',
    icon: '🔍',
    summary: 'The starting point. Understand where AI can have the biggest impact in your business before spending a dollar on implementation.',
    deliverables: [
      'Operations audit across key business functions',
      'AI opportunity identification and prioritization',
      'Tool and workflow mapping',
      'Prioritized implementation roadmap',
      'ROI analysis for top opportunities',
    ],
    ideal: 'Business owners and GMs who are curious about AI but don\'t know where to start.',
  },
  {
    tier: 'Tier 2',
    name: 'AI Strategy Implementation',
    time: 'One-time · 4–8 weeks',
    color: '#0C447C',
    icon: '🗺️',
    summary: 'Move from roadmap to action. Tool selection, implementation, and the people side - role changes and training that make adoption real.',
    deliverables: [
      'Vendor evaluation and selection support',
      'Build-vs-buy decision framework',
      'Implementation oversight and QA',
      'Staff change management planning',
      'Integration architecture review',
    ],
    ideal: 'Companies that have identified AI priorities and need expert guidance to execute without missteps.',
  },
  {
    tier: 'Tier 3',
    name: 'AI Advisor',
    time: 'Ongoing retainer',
    color: '#0A1628',
    icon: '📈',
    summary: 'Your on-call AI strategy partner. We stay embedded in your business to continuously drive automation, monitor performance, and report results to leadership.',
    deliverables: [
      'Monthly strategy review sessions',
      'New automation identification and deployment',
      'Performance monitoring and reporting',
      'Executive AI briefings and dashboards',
      'Vendor and tool management support',
    ],
    ideal: 'Clients who completed an Assessment or Advisory engagement and want to keep the momentum going.',
  },
];

const journey = [
  { step: '01', title: 'Discovery Call', desc: 'Free 30-minute call to understand your business and goals. No jargon.' },
  { step: '02', title: 'Assessment', desc: 'We map your operations, find the AI opportunities, and build your roadmap.' },
  { step: '03', title: 'Advisory', desc: 'Guided implementation - vendor selection, oversight, change management.' },
  { step: '04', title: 'Retainer', desc: 'Ongoing partnership to continuously optimize and expand your AI footprint.' },
];

export default function Services() {
  return (
    <>
      <Helmet>
        <title>AI Consulting Services | Sagient Partners</title>
        <meta name="description" content="AI Readiness Assessments, Strategy Advisory, and Fractional AI Advisor retainers for SMBs. Practical, operator-led AI consulting starting at $3K." />
        <link rel="canonical" href="https://sagientpartners.com/services" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sagientpartners.com/services" />
        <meta property="og:title" content="AI Consulting Services | Sagient Partners" />
        <meta property="og:description" content="AI Readiness Assessments, Strategy Advisory, and Fractional AI Advisor retainers for nonprofits and small businesses. Practical, operator-led AI consulting starting at $3K." />
        <meta property="og:image" content="https://sagientpartners.com/social_card.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="AI Consulting Services | Sagient Partners" />
        <meta name="twitter:description" content="AI Readiness Assessments, Strategy Advisory, and Fractional AI Advisor retainers for nonprofits and small businesses. Practical, operator-led AI consulting starting at $3K." />
        <meta name="twitter:image" content="https://sagientpartners.com/social_card.png" />
      </Helmet>
      {/* Header */}
      <section style={{ background: 'linear-gradient(135deg, #0C447C 0%, #0A1628 100%)', padding: '80px 0 64px', color: '#fff' }}>
        <div className="container">
          <p className="section-label" style={{ color: '#B5D4F4' }}>Services</p>
          <h1 style={{ color: '#fff', maxWidth: 620, marginBottom: 20 }}>A Model for Success</h1>
          <p style={{ color: '#B5D4F4', fontSize: '1.1rem', maxWidth: 580 }}>
            Meet your business where it is. Every engagement is designed to create a natural progression from awareness to action to ongoing growth.
          </p>
        </div>
      </section>

      {/* Client journey */}
      <section className="section section--alt">
        <div className="container">
          <p className="section-label">The Client Journey</p>
          <h2 className="section-heading" style={{ marginBottom: 40 }}>How Engagements Typically Unfold</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 24 }}>
            {journey.map((j, i) => (
              <div key={j.step} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{
                  minWidth: 44, height: 44, borderRadius: '50%',
                  background: '#EBF4FD', color: '#0C447C', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem',
                }}>{j.step}</div>
                <div>
                  <h3 style={{ marginBottom: 6, fontSize: '1rem' }}>{j.title}</h3>
                  <p style={{ fontSize: '0.88rem' }}>{j.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service tiers */}
      <section className="section">
        <div className="container">
          <p className="section-label">Engagement Tiers</p>
          <h2 className="section-heading" style={{ marginBottom: 48 }}>Service Details</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
            {tiers.map(t => (
              <div key={t.tier} className="card" style={{ borderLeft: `5px solid ${t.color}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <span style={{ fontSize: '1.8rem' }}>{t.icon}</span>
                    <span className="tag">{t.tier}</span>
                  </div>
                  <h2 style={{ fontSize: '1.5rem', marginBottom: 8 }}>{t.name}</h2>
                  <p style={{ color: '#378ADD', fontSize: '0.88rem', marginBottom: 20 }}>{t.time}</p>
                  <p style={{ fontSize: '0.95rem', marginBottom: 20 }}>{t.summary}</p>
                  <p style={{ fontSize: '0.85rem', color: '#0C447C', fontStyle: 'italic' }}>
                    <strong>Ideal for:</strong> {t.ideal}
                  </p>
                </div>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 16, color: '#0A1628' }}>What's Included</p>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {t.deliverables.map(d => (
                      <li key={d} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: '0.93rem', color: '#64748B' }}>
                        <span style={{ color: '#378ADD', fontWeight: 700, marginTop: 2 }}>✓</span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: '#EBF4FD', padding: '72px 0', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ color: '#0C447C', marginBottom: 16 }}>Not Sure Where to Start?</h2>
          <p style={{ color: '#64748B', fontSize: '1.05rem', maxWidth: 520, margin: '0 auto 32px' }}>
            Most clients start with an AI Readiness Assessment. It's low-risk, high-insight, and sets the foundation for everything that follows.
          </p>
          <Link to="/contact" className="btn btn-primary">Book a Free Discovery Call →</Link>
        </div>
      </section>
    </>
  );
}
